import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Typography } from 'antd';

import { ProductSection } from '@/features/constructorProducts';
import { QuestionnaireSection } from '@/features/constructor';
import { sortWithNulls } from '@/utils/helpers';
import { SortableItem, SortableList } from '@/components';
import { ConstructorApi as ConstructorQuestionnaireApi } from '@/features/constructor/api';
import { ConstructorApi as ConstructorProductsApi } from '@/features/constructorProducts/api';
import { queryClient } from '@/app/QueryClientProvider';
import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { useParams } from 'react-router';
import { StyledPanel } from '@/features/constructorCommon/styled';
import { usePopups } from '@/hooks';

export const SectionList = ({ isLoading, data, type, ...props }) => {
  const [sections, setSections] = useState([]);
  const { id } = useParams();
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = usePopups();

  async function updateSectionOrder(sectionId, order) {
    const initialSections = [...sections];

    try {
      if (type === 'questionnaire') {
        await ConstructorQuestionnaireApi.updateSectionOrder({
          id: sectionId,
          newNumber: Number(order)
        });
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({ questionSectionTypeId: id })
        });
      }
      if (type === 'products') {
        await ConstructorProductsApi.updateSectionOrder({
          id: sectionId,
          sortOrder: order.toString()
        });
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getProductsSections({ productId: id })
        });
      }
      showSuccessToast('Section order is changed');
    } catch (e) {
      setSections(initialSections);
      showErrorToast(e, true);
    }
  }

  useEffect(() => {
    if (data) {
      setSections(
        data?.sort((a, b) => {
          if (type === 'questionnaire') return sortWithNulls(a.newNumber, b.newNumber);
          if (type === 'products') return sortWithNulls(Number(a.sortOrder), Number(b.sortOrder));
          return 0;
        })
      );
    }
  }, [data]);

  return (
    <>
      {isLoading && <div>{t('Loading')}...</div>}

      <Typography.Title level={4}>{t('Sections')}</Typography.Title>

      {type === 'questionnaire' && sections && (
        <div style={{ margin: '16px 0' }}>
          <SortableList list={sections} setList={setSections} updateOrder={updateSectionOrder}>
            {sections.map((section, index) => (
              <SortableItem
                key={section.id}
                id={section.id}
                handle={true}
                value={section.id}
                isLast={sections.length - 1 === index}
                isFirst={index === 0}
              >
                <Collapse bordered={false}>
                  <Collapse.Panel header={section.section} key={section.id}>
                    <QuestionnaireSection sectionProp={section} {...props} />
                  </Collapse.Panel>
                </Collapse>
              </SortableItem>
            ))}
          </SortableList>
        </div>
      )}
      {type === 'products' && sections && (
        <div style={{ margin: '16px 0' }}>
          <Collapse accordion>
            {sections.map((section) => (
              <StyledPanel header={section.section} key={section.id}>
                <ProductSection sectionProp={section} />
              </StyledPanel>
            ))}
          </Collapse>
        </div>
      )}
    </>
  );
};
